/**
 * usePermission hook
 *
 * Provides a consistent, component-level API for querying the current user's
 * permissions without scattering role-string comparisons across the codebase.
 *
 * Usage:
 *   const { isAdmin, hasRole, canAccess } = usePermission();
 *   if (hasRole("STAFF")) { ... }
 *   if (canAccess("/sellers")) { ... }
 */
import useUserStore from "../store/user";
import {
  UserType,
  isAdminRole,
  isCustomerRole,
  hasRouteAccess,
} from "../permissions";

interface UsePermissionReturn {
  /** The raw userType string of the authenticated user, or null. */
  userType: UserType | null;
  /** True when the current user belongs to the admin-panel group. */
  isAdmin: boolean;
  /** True when the current user is a CUSTOMER. */
  isCustomer: boolean;
  /**
   * Returns true when the current user's role matches `role`.
   * Accepts a single role or an array – any match returns true.
   */
  hasRole: (role: UserType | UserType[]) => boolean;
  /**
   * Returns true when the current user is allowed to visit `path`
   * according to the ROLE_ROUTE_ACCESS matrix.
   */
  canAccess: (path: string) => boolean;
}

export function usePermission(): UsePermissionReturn {
  const { user } = useUserStore();

  const userType = (user?.userType as UserType) ?? null;

  const isAdmin = userType ? isAdminRole(userType) : false;
  const isCustomer = userType ? isCustomerRole(userType) : false;

  function hasRole(role: UserType | UserType[]): boolean {
    if (!userType) return false;
    return Array.isArray(role) ? role.includes(userType) : role === userType;
  }

  function canAccess(path: string): boolean {
    if (!userType) return false;
    return hasRouteAccess(userType, path);
  }

  return { userType, isAdmin, isCustomer, hasRole, canAccess };
}
